import { Loader } from "lucide-react";
import { FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Link } from "react-router";
import { z } from "zod";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  useDeleteSKUMutation,
  useTransferSKUMutation,
  useUpdateSKUMutation,
} from "@/data/mutations";
import { SKU } from "@/data/types";
import { useDialogState } from "@/hooks/use-dialog-state";
import { skuFormSchema, SKUFormValues } from "@/lib/forms";
import { canEditSKU } from "@/lib/permissions";
import useStore from "@/state";

type Props = {
  sku: SKU;
};

export const OverviewTab: FC<Props> = ({ sku }) => {
  const updateSKUMutation = useUpdateSKUMutation(sku.id);
  const transferSKUMutation = useTransferSKUMutation();
  const deleteSKUMutation = useDeleteSKUMutation();
  const { openDialog } = useDialogState();
  const { session } = useStore();

  const form = useFormContext<SKUFormValues>();

  const handleTransferSKU = () => {
    openDialog({ name: "transfer-sku", params: { skuId: sku.id } });
  };

  const handleResequence = () => {
    openDialog({ name: "resequence-sku", params: { skuId: sku.id } });
  };

  const handleDeleteSKU = () => {
    openDialog({ name: "delete-sku", params: { skuId: sku.id } });
  };

  const handleMoveTags = () => {
    openDialog({
      name: "move-tags",
      params: { skuId: sku.id, tagCount: sku.tags_count },
    });
  };

  const onSubmit = async (values: z.infer<typeof skuFormSchema>) => {
    await updateSKUMutation.mutateAsync({
      name: values.name,
      notes: values.notes,
      analytics_url: values.analytics_url,
    });

    form.reset({}, { keepValues: true });
  };

  const lacksPermission = useMemo(() => {
    return !canEditSKU(session?.permissions);
  }, [session]);

  return (
    <div className="w-full">
      <div className="flex gap-6 mt-6">
        <Card className="flex-1 p-6 space-y-6">
          <p className="text-base text-muted-foreground">Tags in SKU</p>
          <p className=" font-medium text-2xl/tight">{sku.tags_count}</p>
          <Link to={`/tags?templateId=${sku.id}`} className="underline">
            View in Tags
          </Link>
        </Card>
        <Card className="flex-1 p-6 space-y-6">
          <p className="text-base text-muted-foreground">Page Views in SKU</p>
          <p className="font-medium text-2xl/tight">
            {sku.tag_view_verifications_count}
          </p>
          <Link
            to={`/verifications?templateId=${sku.id}&verifyType=user_view`}
            className="underline"
          >
            View in Verifications
          </Link>
        </Card>
      </div>

      <h3 className="scroll-m-20 text-2xl font-medium tracking-tight mt-8">
        Details
      </h3>

      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="name"
          disabled={lacksPermission}
          render={({ field }) => (
            <FormItem className="mt-6">
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Spring Trade Show" {...field} />
              </FormControl>
              <FormDescription>
                This is the display name of the SKU and its associated Digital
                Product. Max 75 characters.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="notes"
          disabled={lacksPermission}
          render={({ field }) => (
            <FormItem className="mt-6">
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Input placeholder="Our project with Acme Corp" {...field} />
              </FormControl>
              <FormDescription>
                Use this to add any additional notes about this SKU. This will
                not be displayed publicly.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="analytics_url"
          disabled={lacksPermission}
          render={({ field }) => (
            <FormItem className="mt-6">
              <FormLabel>Looker Analytics</FormLabel>
              <FormControl>
                <Input
                  placeholder="Link to Looker analytics dashboard to view tag activity."
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Accordion type="single" collapsible className="mt-6 border-b">
          <AccordionItem value="danger-zone">
            <AccordionTrigger>Danger Zone</AccordionTrigger>
            <AccordionContent>
              <Card className="p-6">
                <div className="flex justify-between">
                  <div>
                    <p className="font-medium">Transfer</p>
                    <p className="font-medium text-sm text-muted-foreground">
                      Transfer the SKU to a different organization.
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={handleTransferSKU}
                    disabled={transferSKUMutation.isPending || lacksPermission}
                    className="w-[100px]"
                  >
                    {transferSKUMutation.isPending ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : null}
                    Transfer
                  </Button>
                </div>
                <div className="flex justify-between mt-6">
                  <div>
                    <p className="font-medium">Move Tags</p>
                    <p className="font-medium text-sm text-muted-foreground">
                      Choose a group of tags to move from one SKU to another
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={handleMoveTags}
                    disabled={transferSKUMutation.isPending || lacksPermission}
                    className="w-[100px]"
                  >
                    Move
                  </Button>
                </div>
                <div className="flex justify-between mt-6">
                  <div>
                    <p className="font-medium">Resequence</p>
                    <p className="font-medium text-sm text-muted-foreground">
                      Reorder serial numbers of tags in this SKU sequentially by
                      programming time
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleResequence}
                    disabled={deleteSKUMutation.isPending || lacksPermission}
                    className="w-[100px]"
                  >
                    {deleteSKUMutation.isPending ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : null}
                    Resequence
                  </Button>
                </div>
                <div className="flex justify-between mt-6">
                  <div>
                    <p className="font-medium">Delete</p>
                    <p className="font-medium text-sm text-muted-foreground">
                      Delete SKU and all of its associated tags from the
                      organization
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDeleteSKU}
                    disabled={deleteSKUMutation.isPending || lacksPermission}
                    className="w-[100px]"
                  >
                    {deleteSKUMutation.isPending ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : null}
                    Delete
                  </Button>
                </div>
              </Card>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <Button
          className="mt-6"
          type="submit"
          disabled={
            lacksPermission ||
            !form.formState.isDirty ||
            !form.formState.isValid ||
            updateSKUMutation.isPending
          }
        >
          {updateSKUMutation.isPending ? (
            <Loader className="animate-spin" />
          ) : null}
          Save
        </Button>
      </form>
    </div>
  );
};
